import { getToken } from 'firebase/app-check';
import { getAppCheckInstance } from '../firebase/firebaseConfig';

// Flag pour désactiver App Check si cela cause des problèmes
const APP_CHECK_ENABLED = process.env.REACT_APP_APP_CHECK_ENABLED !== 'false';

/**
 * Récupère le token App Check pour l'ajouter aux requêtes API
 * Retourne null si App Check est désactivé ou en cas d'erreur
 */
export const getAppCheckToken = async (): Promise<string | null> => {
  // Si App Check est désactivé, retourner null immédiatement
  if (!APP_CHECK_ENABLED) {
    return null;
  }

  try {
    // Obtenir l'instance App Check via la fonction helper
    const appCheckInstance = getAppCheckInstance();
    
    if (!appCheckInstance) {
      // App Check n'est pas initialisé, retourner null sans erreur
      return null;
    }

    // Wrapper getToken dans une promesse avec gestion complète des erreurs
    return new Promise<string | null>((resolve) => {
      // Timeout pour éviter les attentes infinies
      const timeoutId = setTimeout(() => {
        resolve(null);
      }, 2000); // Timeout réduit à 2 secondes

      // Appeler getToken avec gestion d'erreur complète
      getToken(appCheckInstance, false)
        .then((result) => {
          clearTimeout(timeoutId);
          if (result && result.token) {
            resolve(result.token);
          } else {
            resolve(null);
          }
        })
        .catch((error: any) => {
          clearTimeout(timeoutId);
          // Ignorer toutes les erreurs, y compris "cancelled"
          resolve(null);
        });
    });
  } catch (error: any) {
    // Ignorer toutes les erreurs pour ne pas bloquer l'application
    return null;
  }
};

/**
 * Ajoute le token App Check aux headers d'une requête fetch
 * Gère les erreurs silencieusement pour ne pas bloquer l'application
 */
export const addAppCheckHeader = async (headers: HeadersInit = {}): Promise<HeadersInit> => {
  // Si App Check est désactivé, retourner les headers sans modification
  if (!APP_CHECK_ENABLED) {
    return headers;
  }

  try {
    // Utiliser Promise.resolve pour éviter les erreurs de promesse non gérées
    const token = await Promise.resolve(getAppCheckToken()).catch(() => null);
    
    const headersWithAppCheck = {
      ...headers,
      ...(token && { 'X-Firebase-AppCheck': token })
    };
    
    return headersWithAppCheck;
  } catch (error: any) {
    // En cas d'erreur, retourner les headers sans le token App Check
    // L'application doit continuer à fonctionner même sans App Check
    return headers;
  }
};
